'use client';

import { Check, Copy, KeyRound, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ZapierLogo } from '@/features/integrations/components/zapier-logo';
import { useGenerateIntegrationToken } from '@/features/integrations/hooks/use-generate-integration-token';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';

interface GenerateIntegrationTokenDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: number;
  onSuccess?: (token: string, tokenName: string) => void;
  generatedToken?: string | null;
  generatedTokenName?: string;
}

export const GenerateIntegrationTokenDialog = ({
  open,
  onOpenChange,
  projectId,
  onSuccess,
  generatedToken = null,
  generatedTokenName = '',
}: GenerateIntegrationTokenDialogProps) => {
  const { t } = useTranslation('integrations', {
    keyPrefix: 'GenerateTokenDialog',
  });
  const { t: tSuccess } = useTranslation('integrations', {
    keyPrefix: 'integration_token_management.generate_success_dialog',
  });
  const { t: tCommon } = useTranslation('common');

  const generateIntegrationToken = useGenerateIntegrationToken();
  const [tokenName, setTokenName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleClose = (newOpen: boolean) => {
    if (!newOpen && !isGenerating && !generatedToken) {
      onOpenChange(false);
      setTokenName('');
      setIsGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!generatedToken) return;
    try {
      await navigator.clipboard.writeText(generatedToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = generatedToken;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // Copy failed
      }
      document.body.removeChild(textArea);
    }
  };

  const handleGenerate = () => {
    if (!tokenName.trim()) return;

    setIsGenerating(true);
    generateIntegrationToken.mutate(
      {
        tokenName: tokenName.trim(),
        projectId: projectId,
      },
      {
        onSuccess: (data) => {
          setIsGenerating(false);
          onSuccess?.(data.token, tokenName.trim());
          setTokenName('');
        },
        onError: () => {
          setIsGenerating(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-2xl"
        hideClose={!!generatedToken}
        onInteractOutside={(e) => {
          if (isGenerating || generatedToken) {
            e.preventDefault();
          }
        }}
      >
        {generatedToken ? (
          <>
            <DialogHeader className="flex flex-col items-center gap-3">
              <div className="flex size-20 items-center justify-center rounded-full border border-purple-500/30 bg-gradient-to-b from-purple-500/20 to-purple-600/20">
                <KeyRound className="size-10 text-purple-400" />
              </div>
              <DialogTitle className="text-center text-2xl">
                {tSuccess('title')}
              </DialogTitle>
              <DialogDescription className="p-4 pt-2 text-center text-base text-white/60">
                {tSuccess('description')}
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-2">
              <label className="text-sm text-white/80">
                {generatedTokenName}
              </label>
              <Input
                variant="form"
                value={generatedToken}
                readOnly
                className="font-mono text-sm"
              />
            </div>

            <DialogFooter className="mt-4 flex items-center justify-between">
              <Button
                variant="outline"
                btnType="secondary"
                onClick={() => onOpenChange(false)}
                className="min-w-32"
              >
                {tCommon('button.close')}
              </Button>
              <Button
                variant="greenGradient"
                btnType="secondary"
                onClick={handleCopy}
                className="min-w-32"
              >
                {copied ? (
                  <>
                    <Check className="size-4" />
                    {tSuccess('copied')}
                  </>
                ) : (
                  <>
                    <Copy className="size-4" />
                    {tSuccess('copy_button')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-white">
                {t('title')}
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-8">
              {/* Zapier Logo Section */}
              <div className="flex items-center justify-center">
                <ZapierLogo />
              </div>

              {/* Form Section */}
              <div className="rounded-lg bg-purple-800/20 p-6">
                <h3 className="mb-4 text-xl font-semibold text-white">
                  {t('heading')}
                </h3>
                <DialogDescription className="mb-6 text-sm text-white/60">
                  {t('description')}
                </DialogDescription>
                <div className="flex flex-col gap-2">
                  <label className="text-sm text-white/80">
                    {t('token_name_label')}
                  </label>
                  <Input
                    variant="form"
                    placeholder={t('token_name_placeholder')}
                    value={tokenName}
                    onChange={(e) => setTokenName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && tokenName.trim()) {
                        handleGenerate();
                      }
                    }}
                  />
                </div>
              </div>

              {/* Footer with Generate Button */}
              <DialogFooter>
                <div className="flex w-full justify-end">
                  <Button
                    variant="greenGradient"
                    btnType="secondary"
                    onClick={handleGenerate}
                    disabled={
                      !tokenName.trim() || generateIntegrationToken.isPending
                    }
                    className="min-h-11 min-w-32"
                  >
                    {isGenerating && (
                      <Loader2 className="mr-2 size-4 animate-spin" />
                    )}
                    {isGenerating ? t('loading_text') : t('generate_button')}
                  </Button>
                </div>
              </DialogFooter>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
